import { Transition } from '@headlessui/react'
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useToast as useToastContext } from '@/contexts/ToastContext'
import type { Toast, ToastType } from '@/types/toast'

const iconMap: Record<ToastType, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
}

const colorMap: Record<ToastType, string> = {
  success: 'text-green-400',
  error: 'text-red-400',
  info: 'text-blue-400',
  warning: 'text-yellow-400',
}

export default function ToastContainer() {
  const { toasts, dismissToast } = useToastContext()

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex items-start justify-end px-4 py-6 sm:p-6 z-[100]"
    >
      <div className="flex w-full flex-col items-end space-y-4 sm:max-w-sm">
        {toasts.map((toast: Toast) => {
          const Icon = iconMap[toast.type] || CheckCircleIcon
          const iconColor = colorMap[toast.type] || 'text-green-400'

          return (
            <Transition key={toast.id} show={toast.show}>
              <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition data-closed:data-[leave]:opacity-0 data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0 data-leave:duration-100 data-leave:ease-in">
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="shrink-0">
                      <Icon aria-hidden="true" className={`size-6 ${iconColor}`} />
                    </div>
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                      <p className="text-sm font-medium text-gray-900">{toast.title}</p>
                      {toast.message && (
                        <p className="mt-1 text-sm text-gray-500">{toast.message}</p>
                      )}
                      {toast.action && (
                        <button
                          type="button"
                          onClick={() => { toast.action?.onClick(); dismissToast(toast.id) }}
                          className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-500"
                        >
                          {toast.action.label}
                        </button>
                      )}
                    </div>
                    <div className="ml-4 flex shrink-0">
                      <button
                        type="button"
                        onClick={() => dismissToast(toast.id)}
                        className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon aria-hidden="true" className="size-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          )
        })}
      </div>
    </div>
  )
}
